import axios, { type AxiosResponse } from 'axios'
import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { logger, sendGTMEvent } from 'shared/lib'
import { type IResponseSubscription } from 'entities/Subscription/api/types'
import { type IResponseToken } from 'entities/Auth'
import { getBaseApiParams } from 'entities/Auth/helpers/getBaseApiParams'
import {
  type IGtmAnswers,
  RegisterGtmStepNameEnum,
  RegisterStepEnum,
  RegisterGtmStepNumberEnum,
} from './type'
import { type RegisterStore } from './registerStore'

export class GtmStore {
  private _answers: IGtmAnswers | null = null
  private _disposeUpdatedAnswers: IReactionDisposer | null = null

  constructor() {
    makeAutoObservable(this)

    this.reactionUpdatedAnswers()
  }

  get answers() {
    return this._answers
  }

  get step() {
    return this._answers?.step || null
  }

  get stepName() {
    if (!this.answers) return RegisterGtmStepNameEnum.emailPassword

    switch (this.answers.step) {
      case RegisterStepEnum.stepSignUp:
        return RegisterGtmStepNameEnum.emailPassword
      case RegisterStepEnum.step1:
        return RegisterGtmStepNameEnum.companyInformation
      case RegisterStepEnum.step2:
        return RegisterGtmStepNameEnum.crmSelection
      case RegisterStepEnum.step3:
        return RegisterGtmStepNameEnum.countrySelection
      case RegisterStepEnum.step4:
        return RegisterGtmStepNameEnum.creditCardEntry
      default:
        return RegisterGtmStepNameEnum.emailPassword
    }
  }

  get stepNumber() {
    if (!this.answers) return RegisterGtmStepNumberEnum.emailPassword

    switch (this.answers.step) {
      case RegisterStepEnum.stepSignUp:
        return RegisterGtmStepNumberEnum.emailPassword
      case RegisterStepEnum.step1:
        return RegisterGtmStepNumberEnum.companyInformation
      case RegisterStepEnum.step2:
        return RegisterGtmStepNumberEnum.crmSelection
      case RegisterStepEnum.step3:
        return RegisterGtmStepNumberEnum.countrySelection
      case RegisterStepEnum.step4:
        return RegisterGtmStepNumberEnum.creditCardEntry
      default:
        return RegisterGtmStepNumberEnum.emailPassword
    }
  }

  setAnswers = (data: IGtmAnswers) => {
    this._answers = data
  }

  logEvent = () => {
    sendGTMEvent({
      eventName: 'registration_funnel_step',
      eventParams: {
        step_name: this.stepName,
        step_number: this.stepNumber,
        email: this.answers?.email || null,
        phone: this._answers?.phone || null,
        crm: this._answers?.crm || null,
        contacts_number: this._answers?.contacts_number || null,
        company_size: this._answers?.company_size || null,
        role: this._answers?.role || null,
        first_message_country: this._answers?.first_message_country || null,
        plan: this._answers?.plan || null,
        userId: this._answers?.userId || null,
      },
    })

    if (this.stepNumber === RegisterGtmStepNumberEnum.creditCardEntry) {
      this.logNewAccountEvent()
    }
  }

  logNewAccountEvent = () => {
    sendGTMEvent({
      eventName: 'new_trial',
      eventParams: {
        email: this.answers?.email || null,
        crm: this._answers?.crm || null,
        first_message_country: this._answers?.first_message_country || null,
        plan: this._answers?.plan || null,
        contacts_number: this._answers?.contacts_number || null,
        company_size: this._answers?.company_size || null,
        userId: this._answers?.userId || null,
        phone: this._answers?.phone || null,
      },
    })
  }

  logNewAccount = async (token: IResponseToken, store: RegisterStore) => {
    try {
      const { data }: AxiosResponse<IResponseSubscription> = await axios.get(
        'organization/subscription',
        getBaseApiParams(token)
      )

      if (data && this._answers) {
        this._answers = {
          ...this._answers,
          step: RegisterStepEnum.step4,
          plan: data.plan.id,
          phone: store.step4Store.phoneInputStore.number,
        }
      }
    } catch (e) {
      logger.error(e)
    }
  }

  reactionUpdatedAnswers = () => {
    this._disposeUpdatedAnswers?.()
    this._disposeUpdatedAnswers = reaction(
      () => this.step,
      (step) => {
        if (step) this.logEvent()
      },
      {
        fireImmediately: true,
      }
    )
  }
}
